import * as React from 'react';
import { IDotProps, TDotSize } from '../../Dot';
import { DOT_WIDTH, MAX_DOTS_COUNT, MAX_DOTS_HALF_COUNT } from '../constants';

export const useDots = (totalSlides: number, currentSlide: number) => {
  const slidingShift = React.useMemo(
    () => -Math.min(Math.max(0, currentSlide - MAX_DOTS_HALF_COUNT), Math.max(0, totalSlides - MAX_DOTS_COUNT)),
    [currentSlide, totalSlides],
  );

  const centeringCompensation = React.useMemo(() => {
    return Math.max(0, MAX_DOTS_COUNT - totalSlides) / 2;
  }, [totalSlides]);

  const translateX = React.useMemo(
    () => (slidingShift + centeringCompensation) * DOT_WIDTH,
    [centeringCompensation, slidingShift],
  );

  const dotsConfig = React.useMemo(() => prepareDots(totalSlides, currentSlide), [currentSlide, totalSlides]);

  return { dotsConfig, translateX };
};

function prepareDots(totalSlides: number, currentSlide: number): IDotProps[] {
  const isPaginationBeginning = currentSlide < MAX_DOTS_HALF_COUNT;
  const isPaginationEnds = currentSlide > totalSlides - MAX_DOTS_HALF_COUNT - 1;
  const isAtPaginationCenter = !isPaginationBeginning && !isPaginationEnds;

  return new Array(totalSlides).fill(null).map<IDotProps>((_, i) => {
    if (totalSlides <= 5) {
      return { size: 'L', active: i === currentSlide };
    }

    const dotIndexDiff = Math.abs(currentSlide - i);
    const isDotTrailing = i < currentSlide;
    const isDotLeading = i > currentSlide;

    let size: TDotSize = 'M';
    switch (true) {
      case dotIndexDiff > 3:
      case isPaginationBeginning && isDotTrailing && dotIndexDiff > 1:
      case isPaginationEnds && isDotLeading && dotIndexDiff > 1:
      case isAtPaginationCenter && dotIndexDiff > 2:
        size = 'S';
        break;
      case i === currentSlide:
      case isPaginationBeginning && isDotLeading && dotIndexDiff <= 2:
      case isPaginationEnds && isDotTrailing && dotIndexDiff <= 2:
      case isAtPaginationCenter && dotIndexDiff === 1:
        size = 'L';
        break;
    }

    return { size, active: i === currentSlide };
  });
}
